import { generateBedrockMessage, InputType } from '../utils/helpers/bedrock-messages-helper';
import { AssetResponse, BedrockMessages } from '../utils/interfaces/bedrock-chat.interface';

export namespace SmartFixClient {
  function getCookie(name: string): string | undefined {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();

    return undefined;
  }

  enum SmartFixEndPoint {
    startBoilerSession = 'smart-fix/start',
    deleteBoilerSession = 'smart-fix/end',
    sendBoilerResponse = 'smart-fix/send-response',
    initiateBedrockChat = 'smart-fix/initiate-bedrock-chat',
    initiateBedrockChatOptimized = 'smart-fix/initiate-bedrock-chat-optimized',
    initiateBedrockChatWithDocs = 'smart-fix/initiate-bedrock-chat-CWD',
    sendBedrockChatMessage = 'smart-fix/send-bedrock-chat-message',
    sendBedrockChatMessageOptimized = 'smart-fix/send-bedrock-chat-message-optimized',
    sendBedrockChatMessageChatWithDocs = 'smart-fix/send-bedrock-chat-message-CWD',
    listFoundationModels = 'smart-fix/list-foundation-models',
    markBedrockChatComplete = 'smart-fix/mark-bedrock-chat-complete',
    addBedrockChatFeedback = 'smart-fix/add-bedrock-chat-feedback',
    gatherContextFromUser = 'smart-fix/gather-context-from-user',
    startAgentConversation = 'smart-fix/start-agent-conversation',
    continueAgentConversation = 'smart-fix/continue-agent-conversation',
    recordFireSafetyCheck = 'smart-fix/record-fire-safety-check',
    getElevenLabsSignedUrl = 'smart-fix/get-voice-pre-signed-url',
    voice2VoiceFeedback = 'v1/voice2voice/add-feedback',
  }

  enum SmartFixImageAttachEndPoint {
    analyseImage = 'smart-fix/analyse-bedrock-image', // PII Scanner
    createUploadUrlAndSave = 'smart-fix/create-upload-url', // Upload image to S3 via presigned URL
    saveImageMetaData = 'smart-fix/save-image-metadata', // Save approved-image metadata to dynamo DB
    getChatAssets = 'smart-fix/get-chat-assets',
  }

  export function initialise(type: 'bedrock'): typeof bedRockInitialisation;
  export function initialise(type: 'voice'): typeof Voice2VoiceInitialisation;
  export function initialise(type: 'bedrock' | 'voice') {
    if (type === 'bedrock') {
      return bedRockInitialisation;
    }

    if (type === 'voice') {
      return Voice2VoiceInitialisation;
    }

    throw Error('Invalid type provided for SmartFixClient initialisation');
  }

  function bedRockInitialisation(
    config: {
      environment: string;
      appName: string;
    },
    data: {
      productType: string;
      planNumber: string;
      uploadSessionId: string;
    },
    loadingState: (loading: boolean) => void,
    responseState: (response: BedrockMessages) => void,
    errorState: (error: any) => void
  ) {
    const url = config.environment;
    const controller = new AbortController();

    const bedrockFunctions = {
      sessionId: undefined,
      messages: [] as BedrockMessages[],
      startSession: (
        initialisedCallback: () => void,
        solutionSelected: string,
        summary?: string,
        sessionId?: string
      ) => {
        loadingState(true);

        let endpoint: SmartFixEndPoint;
        switch (solutionSelected) {
          case 'rag':
            endpoint = SmartFixEndPoint.initiateBedrockChat;
            break;
          case 'chat-with-docs':
            endpoint = SmartFixEndPoint.initiateBedrockChatWithDocs;
            break;
          default:
            endpoint = SmartFixEndPoint.initiateBedrockChat;
        }

        fetch(`${url}/${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('AWS_IDT')}`,
          },
          signal: controller.signal,
          body: JSON.stringify({
            text: summary ?? 'Hello',
            productType: data.productType,
            planNumber: data.planNumber,
            sessionId: sessionId,
          }),
        })
          .then(async (response) => {
            const returnedData = await response.json();
            bedrockFunctions.sessionId = returnedData.sessionId;
            const newEntry: BedrockMessages = {
              role: 'agent',
              sessionId: bedrockFunctions.sessionId || '',
              content: {
                message: returnedData.output.text,
                type: 'text',
                timestamp: new Date().toISOString(),
              },
            };

            bedrockFunctions.messages.push(newEntry);
            responseState(newEntry);
            loadingState(false);

            initialisedCallback();
            return newEntry;
          })
          .catch((error) => {
            errorState(error);
            loadingState(false);
          });
      },
      sendMessage: (message: string, solutionSelected: string, inputType?: InputType) => {
        loadingState(true);

        const userEntry = generateBedrockMessage('user', bedrockFunctions.sessionId || '', {
          message: message,
          type: inputType || 'text',
        });

        bedrockFunctions.messages.push(userEntry);
        let endpoint: SmartFixEndPoint;

        switch (solutionSelected) {
          case 'rag':
            endpoint = SmartFixEndPoint.sendBedrockChatMessage;
            break;
          case 'chat-with-docs':
            endpoint = SmartFixEndPoint.sendBedrockChatMessageChatWithDocs;
            break;
          default:
            endpoint = SmartFixEndPoint.sendBedrockChatMessage;
        }

        return fetch(`${url}/${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('AWS_IDT')}`,
          },
          body: JSON.stringify({
            sessionId: bedrockFunctions.sessionId,
            text: message,
          }),
          signal: controller.signal,
        })
          .then(async (response) => {
            const returnedData = await response.json();

            const agentResponse = generateBedrockMessage('agent', bedrockFunctions.sessionId || '', {
              message: returnedData.output.text,
              type: inputType || 'text',
            });

            bedrockFunctions.messages.push(agentResponse);
            responseState(agentResponse);
            loadingState(false);

            return agentResponse;
          })
          .catch((error) => {
            errorState(error);
            loadingState(false);
          });
      },
      analyseImage: (
        message: string,
        image: { base64: string; filename?: string; mimeType?: string },
        productType?: string
      ) => {
        loadingState(true);
        return fetch(`${url}/${SmartFixImageAttachEndPoint.analyseImage}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('AWS_IDT')}`,
          },
          body: JSON.stringify({
            sessionId: bedrockFunctions.sessionId,
            text: message,
            image: image,
            productType: productType,
          }),
          signal: controller.signal,
        })
          .then(async (response) => {
            const returnedData = await response.json();

            if (returnedData.status === 'blocked' || returnedData['amazon-bedrock-guardrailAction'] === 'INTERVENED') {
              loadingState(false);
              return { blocked: true };
            }

            const agentResponse: BedrockMessages = {
              role: 'agent',
              sessionId: bedrockFunctions.sessionId || '',
              content: {
                message: returnedData.content[0].text,
                type: 'text',
                timestamp: new Date().toISOString(),
              },
            };

            loadingState(false);

            return agentResponse;
          })
          .catch((error) => {
            errorState(error);
            loadingState(false);
          });
      },

      sendFeedback: (
        rating: string,
        content: string,
        isPositive?: boolean,
        payload?: { surveyFeedbackResponses?: Record<string, string | boolean | number | null> }
      ) => {
        loadingState(true);
        return fetch(`${url}/${SmartFixEndPoint.addBedrockChatFeedback}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('AWS_IDT')}`,
          },
          body: JSON.stringify({
            rating,
            content,
            sessionId: bedrockFunctions.sessionId,
            ...(isPositive === undefined ? {} : { isPositive }),
            ...(payload ?? {}),
          }),
        }).catch((error) => {
          errorState(error);
          loadingState(false);
        });
      },
      recordFireSafetyCheck: (planNumber: string) => {
        loadingState(true);
        return fetch(`${url}/${SmartFixEndPoint.recordFireSafetyCheck}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('AWS_IDT')}`,
          },
          body: JSON.stringify({
            planNumber,
          }),
        })
          .then(() => {
            loadingState(false);
          })
          .catch((error) => {
            errorState(error);
            loadingState(false);
          });
      },
      markChatAsComplete: () => {
        loadingState(true);
        return fetch(`${url}/${SmartFixEndPoint.markBedrockChatComplete}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('AWS_IDT')}`,
          },
          body: JSON.stringify({
            sessionId: bedrockFunctions.sessionId,
          }),
        }).catch((error) => {
          errorState(error);
          loadingState(false);
        });
      },
      gatherContextFromUser: (payload: {
        answers: { question: string; answer: string; inputType?: InputType }[];
        answer?: string;
        productType?: string;
        brand?: string;
        modelNumber?: string;
        sessionId?: string;
        imageAttachEnabled?: boolean;
      }) => {
        loadingState(true);
        return fetch(`${url}/${SmartFixEndPoint.gatherContextFromUser}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('AWS_IDT')}`,
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        })
          .then(async (response) => {
            loadingState(false);
            return response.json() as Promise<{
              done: boolean;
              answers: { question: string; answer: string; inputType?: InputType }[];
              nextQuestion?: string;
              reAsk?: string;
              summary?: string;
              greeting?: string;
              sessionId?: string;
            }>;
          })
          .catch((error) => {
            throw error;
          });
      },
      startAgentConversation: (
        summary: string,
        brand: string,
        sessionId: string,
        productType: string,
        voiceToVoiceTranscript?: string,
        voiceSessionId?: string
      ) => {
        loadingState(true);
        return fetch(`${url}/${SmartFixEndPoint.startAgentConversation}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('AWS_IDT')}`,
          },
          body: JSON.stringify({
            summary,
            brand,
            productType,
            sessionId,
            voiceToVoiceTranscript,
            voiceSessionId,
            planNumber: data.planNumber,
          }),
        })
          .then(async (response) => {
            loadingState(false);
            if (!response.ok) throw new Error('Failed to start agent conversation');

            const returnedData = await response.json();
            bedrockFunctions.sessionId = returnedData.sessionId;

            return returnedData;
          })
          .catch((error) => {
            errorState(error);
            loadingState(false);
          });
      },
      continueAgentConversation: (text: string, productType: string, inputType?: InputType) => {
        loadingState(true);
        const userEntry = generateBedrockMessage('user', bedrockFunctions.sessionId || '', {
          message: text,
          type: inputType || 'text',
        });

        bedrockFunctions.messages.push(userEntry);

        return fetch(`${url}/${SmartFixEndPoint.continueAgentConversation}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('AWS_IDT')}`,
          },
          body: JSON.stringify({
            sessionId: bedrockFunctions.sessionId,
            productType: productType,
            text,
            type: inputType || text,
          }),
        })
          .then(async (response) => {
            loadingState(false);
            const returnedData = await response.json();
            const agentResponse = generateBedrockMessage('agent', bedrockFunctions.sessionId || '', {
              message: returnedData.response,
              type: inputType || 'text',
            });
            bedrockFunctions.messages.push(agentResponse);
            responseState(agentResponse);
            loadingState(false);
          })
          .catch((error) => {
            errorState(error);
            loadingState(false);
          });
      },
      // create upload url and upload to s3 bucket
      createUploadUrlAndSave: async (file: File) => {
        let fileKey: string | undefined;
        loadingState(true);
        return fetch(`${url}/${SmartFixImageAttachEndPoint.createUploadUrlAndSave}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('AWS_IDT')}`,
          },
        })
          .then(async (response) => {
            loadingState(true);
            const returnedData = await response.json();
            const uploadURl = returnedData.uploadUrl;
            fileKey = returnedData.key;

            // this is doing the upload to the returned URL
            loadingState(true);
            await fetch(uploadURl, {
              method: 'PUT',
              headers: {
                'Content-Type': file.type,
              },
              body: file,
            });
            loadingState(true);
            return { fileKey, contentType: file.type }; // Return the key and content type
          })
          .catch((error) => {
            errorState(error);
            loadingState(false);
          });
      },
      saveImageMetaData: (
        fileKey: string,
        contentType: string,
        planNumber: string,
        solution: string,
        productType: string
      ) => {
        loadingState(true);
        return fetch(`${url}/${SmartFixImageAttachEndPoint.saveImageMetaData}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('AWS_IDT')}`,
          },
          body: JSON.stringify({
            fileKey: fileKey,
            contentType,
            planNumber,
            solution,
            productType,
          }),
        }).then(async (response: any) => {
          const returnedData = await response.json();
          data.uploadSessionId = returnedData.uploadSessionId;
        });
      },
      getChatAssets: async () => {
        return fetch(
          `${url}/${SmartFixImageAttachEndPoint.getChatAssets}?uploadSessionId=${encodeURIComponent(data.uploadSessionId)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getCookie('AWS_IDT')}`,
            },
          }
        ).then(async (response) => {
          loadingState(true);
          const returnedData: AssetResponse = await response.json();
          const signedUrl = returnedData.assets[0].signedUrl;
          return signedUrl;
        });
      },
      cancel: () => {
        controller.abort();
      },
    };
    return bedrockFunctions;
  }

  function Voice2VoiceInitialisation(
    config: {
      environment: string;
      appName: string;
    },
    data: {
      productType: string;
      planNumber: string;
      uploadSessionId: string;
    },
    loadingState: (loading: boolean) => void,
    errorState: (error: any) => void
  ) {
    const url = config.environment;

    const voiceFunctions = {
      sessionId: undefined as string | undefined,
      sendFeedback: (
        rating: string,
        content: string,
        isPositive?: boolean,
        payload?: { surveyFeedbackResponses?: Record<string, string | boolean | number | null> }
      ) => {
        loadingState(true);
        return fetch(`${url}/${SmartFixEndPoint.voice2VoiceFeedback}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('AWS_IDT')}`,
          },
          body: JSON.stringify({
            rating,
            content,
            ...(isPositive === undefined ? {} : { isPositive }),
            ...(payload ?? {}),
            sessionId: `${new Date().getTime()}-${data.planNumber}`,
            planNumber: data.planNumber,
            isVoice2VoiceInteraction: true,
          }),
        })
          .then((response) => {
            loadingState(false);
            return response;
          })
          .catch((error) => {
            console.error('Error sending voice2voice feedback:', error);
            errorState(error);
            loadingState(false);
          });
      },
    };
    return voiceFunctions;
  }
}
